import { FC, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { PremiumPrice } from 'src/api/internal/schema';
import {
  PRESENTATION_SCREEN_PRONUNCIATION_FREE_ATTEMPTS_PER_DAY,
  REVIEW_CLASSIC_FREE_WORDS_PER_DAY,
} from 'src/api/monetisation/freeTokenLimits';
import { Icon } from 'src/components';
import { getPricePerMonth } from 'src/utils/payment/paymentPlansUtils';
import theme from 'src/utils/theme';
import { StepPage } from '../StepLayout';
import * as S from './styles';

export const ONBOARDING_PLANS_STEP = 4;

const TERMS_URL = 'https://www.memrise.com/terms';
const PRIVACY_URL = 'https://www.memrise.com/privacy';

const msgs = defineMessages({
  title: {
    id: 'blob_onboarding_plans_title',
    defaultMessage: 'Get to everyday chats by {month} with Pro',
  },
  plansTitle: {
    id: 'comparison_plans_title',
    defaultMessage: 'Sound more natural with Memrise Pro',
  },
  free: { id: 'comparison_plans_free', defaultMessage: 'Free' },
  pro: { id: 'comparison_plans_pro', defaultMessage: 'Pro' },
  whatProAdds: {
    id: 'blob_onboarding_plans_what_pro_adds',
    defaultMessage: 'What Pro adds to your path',
  },
  whatYouGetFree: {
    id: 'comparison_plans_what_you_get_free',
    defaultMessage: 'What you get for free',
  },
  unlimitedReview: { id: 'comparison_plans_unlimited_review', defaultMessage: 'Unlimited review' },
  unlimitedReviewDetail: {
    id: 'blob_onboarding_plans_unlimited_review_detail',
    defaultMessage:
      'Keep all {wordsPerDay} new words a day fresh. Free stops at {limit} reviews a day.',
  },
  review: { id: 'comparison_plans_review', defaultMessage: 'Review' },
  reviewLimit: {
    id: 'comparison_plans_review_limit',
    defaultMessage:
      '{count, plural, one {Limited to # word per day} other {Limited to # words per day}}',
  },
  unlimitedPronunciation: {
    id: 'comparison_plans_unlimited_pronunciation',
    defaultMessage: 'Unlimited pronunciation mode',
  },
  unlimitedPronunciationDetail: {
    id: 'blob_onboarding_plans_unlimited_pronunciation_detail',
    defaultMessage: 'Practise sounding like the locals in the videos, as often as you like',
  },
  pronunciation: { id: 'comparison_plans_pronunciation', defaultMessage: 'Pronunciation mode' },
  reviewLimitTitle: {
    id: 'blob_onboarding_plans_review_limit_title',
    defaultMessage:
      '{count, plural, one {Review: up to # word per day} other {Review: up to # words per day}}',
  },
  pronunciationLimitTitle: {
    id: 'blob_onboarding_plans_pronunciation_limit_title',
    defaultMessage:
      '{count, plural, one {Pronunciation mode: up to # attempt per day} other {Pronunciation mode: up to # attempts per day}}',
  },
  pronunciationLimit: {
    id: 'comparison_plans_pronunciation_limit',
    defaultMessage:
      '{count, plural, one {Limited to # attempt per day} other {Limited to # attempts per day}}',
  },
  speedReview: { id: 'comparison_plans_speed_review', defaultMessage: 'Speed review' },
  speedReviewDetail: {
    id: 'blob_onboarding_plans_speed_review_detail',
    defaultMessage: 'Stay on track for {month} on busy days',
  },
  plansSpeedReviewDetail: {
    id: 'comparison_plans_speed_review_detail',
    defaultMessage: "Race through reviews when you're short on time",
  },
  difficultWords: { id: 'comparison_plans_difficult_words', defaultMessage: 'Difficult words' },
  difficultWordsDetail: {
    id: 'blob_onboarding_plans_difficult_words_detail',
    defaultMessage: 'Drill the {language} words you keep getting wrong',
  },
  customWordLists: {
    id: 'blob_onboarding_plans_custom_word_lists',
    defaultMessage: 'Custom word lists',
  },
  customWordListsDetail: {
    id: 'blob_onboarding_plans_custom_word_lists_detail',
    defaultMessage: 'Make lists for family dinners, birthdays and catch-ups',
  },
  notIncluded: { id: 'comparison_plans_not_included', defaultMessage: 'Not included' },
  choosePlan: { id: 'blob_onboarding_plans_choose_plan', defaultMessage: 'Choose your Pro plan' },
  lifetime: { id: 'ea_plans_lifetime', defaultMessage: 'Lifetime' },
  annual: { id: 'ea_plans_annual', defaultMessage: 'Annual' },
  monthly: { id: 'ea_plans_monthly', defaultMessage: 'Monthly' },
  percentOff: { id: 'blob_onboarding_plans_percent_off', defaultMessage: '{percent}% off' },
  bestValue: { id: 'new_plans_card_best_value', defaultMessage: 'Best value' },
  lifetimeDetail: {
    id: 'commitment_paywall_one_payment',
    defaultMessage: 'One payment, yours forever',
  },
  annualDetail: {
    id: 'blob_onboarding_plans_annual_detail',
    defaultMessage: 'Billed once a year',
  },
  monthlyDetail: {
    id: 'condensed_plans_card_monthly_sub',
    defaultMessage: 'Flexible, cancel anytime',
  },
  pricePerMonth: { id: 'blob_onboarding_plans_price_per_month', defaultMessage: '{price} / month' },
  perMonth: { id: 'blob_onboarding_plans_per_month', defaultMessage: 'per month' },
  cta: { id: 'blob_onboarding_plans_cta', defaultMessage: 'Start learning with {plan}' },
  freeCta: { id: 'blob_onboarding_plans_free_cta', defaultMessage: 'Start learning on Free' },
  plansCta: { id: 'comparison_plans_cta', defaultMessage: 'Get {plan} for {price}' },
  plansFreeCta: {
    id: 'commitment_paywall_keep_learning',
    defaultMessage: 'Keep learning for free',
  },
  lifetimeNote: {
    id: 'comparison_plans_lifetime_note',
    defaultMessage: 'One-time payment. No subscription.',
  },
  annualNote: {
    id: 'comparison_plans_annual_note',
    defaultMessage: 'Billed once a year. Cancel anytime.',
  },
  monthlyNote: {
    id: 'comparison_plans_monthly_note',
    defaultMessage: 'Billed monthly. Cancel anytime.',
  },
  terms: { id: 'comparison_plans_terms', defaultMessage: 'Terms' },
  privacy: { id: 'blob_onboarding_plans_privacy', defaultMessage: 'Privacy' },
  checkoutError: {
    id: 'blob_onboarding_plans_checkout_error',
    defaultMessage: 'We couldn’t open the checkout. Please try again.',
  },
});

export type PlanTab = 'free' | 'pro';

const PLAN_COPY = {
  lifetime: { name: msgs.lifetime, detail: msgs.lifetimeDetail, note: msgs.lifetimeNote },
  year: { name: msgs.annual, detail: msgs.annualDetail, note: msgs.annualNote },
  month: { name: msgs.monthly, detail: msgs.monthlyDetail, note: msgs.monthlyNote },
} as const;

export type PlanPeriod = keyof typeof PLAN_COPY;

const PLAN_ORDER: readonly PlanPeriod[] = ['lifetime', 'year', 'month'];

/** The prices the page offers (lifetime, annual and monthly), in display order */
export const getBlobPlanPrices = (prices: PremiumPrice[] = []): PremiumPrice[] =>
  PLAN_ORDER.map(period => prices.find(price => price.paymentPeriod === period)).filter(
    (price): price is PremiumPrice => !!price,
  );

const getPlanCopy = (price: PremiumPrice) => PLAN_COPY[price.paymentPeriod as PlanPeriod];

/**
 * `onboarding` is the last onboarding step: step progress, and copy built on when the
 * learner's daily goal gets them chatting. `plans` is /payment/plans for blob learners,
 * who can be anywhere on their path, so its copy doesn't promise a date.
 */
type VariantProps =
  { variant?: 'onboarding'; readyDate: Date } | { variant: 'plans'; readyDate?: never };

type PlansComponentProps = VariantProps & {
  /** Lifetime, annual and monthly prices only, in display order */
  prices: PremiumPrice[];
  selectedPrice: PremiumPrice;
  wordsPerDay: number;
  language?: string;
  hasCheckoutError?: boolean;
  onSelectPrice: (price: PremiumPrice) => void;
  onTabChange: (tab: PlanTab) => void;
  onSubscribe: () => void;
  /** Leave out to hide the free button */
  onContinueFree?: () => void;
  onBack?: () => void;
};

export const PlansComponent: FC<PlansComponentProps> = ({
  variant = 'onboarding',
  prices,
  selectedPrice,
  readyDate,
  wordsPerDay,
  language = '',
  hasCheckoutError = false,
  onSelectPrice,
  onTabChange,
  onSubscribe,
  onContinueFree,
  onBack,
}) => {
  const intl = useIntl();
  const [tab, setTab] = useState<PlanTab>('pro');
  // Small screens drop the line under each feature so the plans fit without scrolling.
  const isCompact = useMediaQuery({ maxWidth: theme.sz.lg });
  const isOnboarding = variant === 'onboarding';
  const month = readyDate ? intl.formatDate(readyDate, { month: 'long' }) : '';

  const formatAmount = (amount: number, currency: string) =>
    intl.formatNumber(amount, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const changeTab = (nextTab: PlanTab) => {
    if (nextTab === tab) return;
    setTab(nextTab);
    onTabChange(nextTab);
  };

  const proFeatures = [
    {
      id: 'review',
      title: msgs.unlimitedReview,
      detail: intl.formatMessage(msgs.unlimitedReviewDetail, {
        wordsPerDay,
        limit: REVIEW_CLASSIC_FREE_WORDS_PER_DAY,
      }),
    },
    {
      id: 'pronunciation',
      title: msgs.unlimitedPronunciation,
      detail: intl.formatMessage(msgs.unlimitedPronunciationDetail),
    },
    {
      id: 'speed-review',
      title: msgs.speedReview,
      detail: isOnboarding
        ? intl.formatMessage(msgs.speedReviewDetail, { month })
        : intl.formatMessage(msgs.plansSpeedReviewDetail),
    },
    {
      id: 'difficult-words',
      title: msgs.difficultWords,
      detail: intl.formatMessage(msgs.difficultWordsDetail, { language }),
    },
    {
      id: 'custom-word-lists',
      title: msgs.customWordLists,
      detail: intl.formatMessage(msgs.customWordListsDetail),
    },
  ];

  const notIncluded = intl.formatMessage(msgs.notIncluded);
  const freeFeatures = [
    {
      id: 'review',
      title: msgs.review,
      detail: intl.formatMessage(msgs.reviewLimit, { count: REVIEW_CLASSIC_FREE_WORDS_PER_DAY }),
      compactTitle: intl.formatMessage(msgs.reviewLimitTitle, {
        count: REVIEW_CLASSIC_FREE_WORDS_PER_DAY,
      }),
      included: true,
    },
    {
      id: 'pronunciation',
      title: msgs.pronunciation,
      detail: intl.formatMessage(msgs.pronunciationLimit, {
        count: PRESENTATION_SCREEN_PRONUNCIATION_FREE_ATTEMPTS_PER_DAY,
      }),
      compactTitle: intl.formatMessage(msgs.pronunciationLimitTitle, {
        count: PRESENTATION_SCREEN_PRONUNCIATION_FREE_ATTEMPTS_PER_DAY,
      }),
      included: true,
    },
    { id: 'speed-review', title: msgs.speedReview, detail: notIncluded, included: false },
    { id: 'difficult-words', title: msgs.difficultWords, detail: notIncluded, included: false },
    { id: 'custom-word-lists', title: msgs.customWordLists, detail: notIncluded, included: false },
  ];

  const renderPriceSide = (price: PremiumPrice) => {
    const finalPrice = formatAmount(price.finalPrice, price.currency);
    if (price.paymentPeriod === 'lifetime') {
      return (
        <>
          <S.Price>{finalPrice}</S.Price>
          {price.fullPrice > price.finalPrice && (
            <S.WasPrice>{formatAmount(price.fullPrice, price.currency)}</S.WasPrice>
          )}
        </>
      );
    }
    if (price.paymentPeriod === 'year') {
      return (
        <>
          <S.Price>{finalPrice}</S.Price>
          <S.PriceNote>
            {intl.formatMessage(msgs.pricePerMonth, {
              price: formatAmount(getPricePerMonth(price.finalPrice), price.currency),
            })}
          </S.PriceNote>
        </>
      );
    }
    return (
      <>
        <S.Price>{finalPrice}</S.Price>
        <S.PriceNote>{intl.formatMessage(msgs.perMonth)}</S.PriceNote>
      </>
    );
  };

  const renderBadge = (price: PremiumPrice) => {
    if (price.paymentPeriod === 'lifetime' && price.fullPrice > price.finalPrice) {
      const percent = Math.round(((price.fullPrice - price.finalPrice) / price.fullPrice) * 100);
      return <S.Badge>{intl.formatMessage(msgs.percentOff, { percent })}</S.Badge>;
    }
    if (price.paymentPeriod === 'year') {
      return <S.Badge>{intl.formatMessage(msgs.bestValue)}</S.Badge>;
    }
    return null;
  };

  const selectedCopy = getPlanCopy(selectedPrice);
  const selectedPlanName = intl.formatMessage(selectedCopy.name);

  return (
    <StepPage currentStep={isOnboarding ? ONBOARDING_PLANS_STEP : undefined} onBack={onBack}>
      <S.Columns>
        <S.Pitch>
          <S.Title>
            {isOnboarding
              ? intl.formatMessage(msgs.title, { month })
              : intl.formatMessage(msgs.plansTitle)}
          </S.Title>
          <S.Tabs role="tablist" aria-label={intl.formatMessage(msgs.choosePlan)}>
            {(['free', 'pro'] as const).map(tabName => (
              <S.Tab
                key={tabName}
                type="button"
                role="tab"
                aria-selected={tab === tabName}
                $tab={tabName}
                $selected={tab === tabName}
                onClick={() => changeTab(tabName)}
                data-testid={`plans-tab-${tabName}`}
              >
                {intl.formatMessage(tabName === 'pro' ? msgs.pro : msgs.free)}
              </S.Tab>
            ))}
          </S.Tabs>
          <S.FeaturesHeading>
            {intl.formatMessage(tab === 'pro' ? msgs.whatProAdds : msgs.whatYouGetFree)}
          </S.FeaturesHeading>
          <S.Features role="tabpanel" data-testid={`plans-features-${tab}`}>
            {tab === 'pro'
              ? proFeatures.map(feature => (
                  <S.Feature key={feature.id}>
                    <S.FeatureIcon $variant="included">
                      <Icon name="check" />
                    </S.FeatureIcon>
                    <S.FeatureText>
                      <S.FeatureTitle>{intl.formatMessage(feature.title)}</S.FeatureTitle>
                      {!isCompact && <S.FeatureDetail>{feature.detail}</S.FeatureDetail>}
                    </S.FeatureText>
                  </S.Feature>
                ))
              : freeFeatures.map(feature => (
                  <S.Feature key={feature.id}>
                    <S.FeatureIcon $variant={feature.included ? 'limited' : 'excluded'}>
                      {feature.included ? <S.Dash /> : <Icon name="close" />}
                    </S.FeatureIcon>
                    <S.FeatureText>
                      <S.FeatureTitle $muted={!feature.included}>
                        {isCompact && feature.compactTitle
                          ? feature.compactTitle
                          : intl.formatMessage(feature.title)}
                      </S.FeatureTitle>
                      {!isCompact && <S.FeatureDetail>{feature.detail}</S.FeatureDetail>}
                    </S.FeatureText>
                  </S.Feature>
                ))}
          </S.Features>
        </S.Pitch>

        <S.PlanCard>
          <S.PlanCardTitle>{intl.formatMessage(msgs.choosePlan)}</S.PlanCardTitle>
          <S.PlanOptions role="radiogroup" aria-label={intl.formatMessage(msgs.choosePlan)}>
            {prices.map(price => {
              const isSelected = selectedPrice.key === price.key;
              const copy = getPlanCopy(price);
              return (
                <S.PlanOption
                  key={price.key}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  $selected={isSelected}
                  onClick={() => onSelectPrice(price)}
                  data-testid={`plan-option-${price.paymentPeriod}`}
                >
                  <S.Radio $selected={isSelected} />
                  <S.PlanText>
                    <S.PlanNameRow>
                      <S.PlanName>{intl.formatMessage(copy.name)}</S.PlanName>
                      {renderBadge(price)}
                    </S.PlanNameRow>
                    <S.PlanDetail>{intl.formatMessage(copy.detail)}</S.PlanDetail>
                  </S.PlanText>
                  <S.PriceSide>{renderPriceSide(price)}</S.PriceSide>
                </S.PlanOption>
              );
            })}
          </S.PlanOptions>
          <S.Checkout>
            <S.Actions>
              <S.SubscribeButton
                type="button"
                onClick={() => onSubscribe()}
                data-testid="plans-subscribe"
              >
                {isOnboarding
                  ? intl.formatMessage(msgs.cta, { plan: selectedPlanName })
                  : intl.formatMessage(msgs.plansCta, {
                      plan: selectedPlanName,
                      price: formatAmount(selectedPrice.finalPrice, selectedPrice.currency),
                    })}
              </S.SubscribeButton>
              {onContinueFree && (
                <S.FreeButton
                  type="button"
                  onClick={onContinueFree}
                  data-testid="plans-continue-free"
                >
                  {intl.formatMessage(isOnboarding ? msgs.freeCta : msgs.plansFreeCta)}
                </S.FreeButton>
              )}
              {hasCheckoutError && (
                <S.ErrorText role="alert">{intl.formatMessage(msgs.checkoutError)}</S.ErrorText>
              )}
            </S.Actions>
            <S.Legal>
              {intl.formatMessage(selectedCopy.note)}{' '}
              <S.LegalLink href={TERMS_URL} target="_blank" rel="noopener noreferrer">
                {intl.formatMessage(msgs.terms)}
              </S.LegalLink>
              {' · '}
              <S.LegalLink href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                {intl.formatMessage(msgs.privacy)}
              </S.LegalLink>
            </S.Legal>
          </S.Checkout>
        </S.PlanCard>
      </S.Columns>
    </StepPage>
  );
};
